/**
 * Application op-registry shared core (deep SAFETY CORE).
 *
 * The generic staged-pipeline foundation every structure-creating op builds on:
 * the cm→mm branded choke-point, the injected geometry/commit ports, the
 * shared resolve→preview→commit helpers, and the discriminated Result types.
 * Each op's own resolve/preview/commit (in its feature module) layers its
 * numeric invariants and mints its OWN pipeline brands on top, so per-op
 * golden-test isolation and the structural preview==apply guarantee are intact.
 *
 * The GPU/engine and the structure-creation choreography are SYSTEM
 * BOUNDARIES, injected as ports; this module is React/Recoil/GPU-free.
 */
import { cmToMm } from '~/utils/units';
import { validateStructureName } from '~/utils/validation';
import { resolveRoi, isPointRoiType } from './roiResolver';
import type { AmbiguityReason, ListedStructure } from '../../contract';

/* ── Nominal brands (compile-time phantom; never a runtime property) ── */

declare const brand: unique symbol;
/** A nominal brand: `T` plus a phantom tag no caller can forge without an
 *  explicit (lint-visible) cast. Used for units AND pipeline stages so a
 *  mutation cannot skip resolve/preview or pass a raw number as mm. */
export type Brand<T, B extends string> = T & { readonly [brand]: B };

/** Millimetres in the domain. Only the cm→mm choke-point can mint this. */
export type Mm = Brand<number, 'Mm'>;

/** THE single audited cm→mm boundary (delegates the formula to utils). */
export function cmToMmChokepoint(marginCm: number): Mm {
    return cmToMm(marginCm) as Mm;
}

/* ── Ports (system boundaries — injected; faked in unit tests) ── */

/** Opaque computed geometry. The SAME object flows preview→commit. Unbranded:
 *  the engine `apply*` methods stay distinctly named, so this carries no safety
 *  load (`commitComputed<C>` is generic over it). */
export interface ComputedGeometry {
    readonly resultVolumeCm3: number;
    readonly handle: unknown;
}

export interface CreatedStructure {
    readonly roiNumber: number;
    readonly volumeCm3: number;
}

/** The read projection — richer than the model-facing `ListedStructure`
 *  (adds domain facts the safety core validates as defence-in-depth). */
export interface StructureFacts {
    readonly roiNumber: number;
    readonly name: string;
    readonly type: string;
    readonly volumeCm3: number;
    readonly isEmpty: boolean;
}

/**
 * Render-only preview port shared by every op. `show` publishes a translucent
 * overlay of the computed geometry; `hide` clears it. Implementations BORROW
 * the geometry handle and must never delete it.
 */
export interface GeometryPreviewPort<C> {
    show(computed: C): void;
    hide(): void;
}

/** The geometry-port members every op shares (each op intersects in its own
 *  distinct `apply*` method on top of this). */
export interface BaseGeometryPort {
    /**
     * Free a computed geometry handle that will not be committed — the
     * empty-result branch, or a rejected/cancelled proposal. Optional so
     * unit fakes need not model GPU memory; the engine deps wire it to
     * `ContourTexture.delete`.
     */
    releaseComputed?(handle: unknown): void;
}

/**
 * The deps shell every structure-creating op shares. Each op's exported
 * `*Deps` intersects this with its own distinctly-named `apply*` method (which
 * re-narrows `geometry`), so the op-specific call stays statically distinct —
 * `previewSymmetricMargin` cannot call `applyRing`.
 */
export interface OpDeps<C extends ComputedGeometry> {
    readonly structures: ReadonlyArray<StructureFacts>;
    readonly geometry: BaseGeometryPort;
    readonly commit: {
        createNewStructure(
            name: string,
            computed: C
        ): Promise<CreatedStructure>;
        /**
         * Identity of the structure set the geometry targets, read live.
         * `gateProposal` captures it at plan time and re-checks at Apply so a
         * set switch between preview and Apply is refused, never written into
         * the wrong frame. Optional: unit fakes omit it (the guard no-ops); the
         * engine seam wires it to the active structure set's sopInstanceUID.
         */
        currentContextId?(): string | undefined;
    };
    /**
     * Optional render-only port: show/hide a translucent overlay of the
     * already-computed geometry while the clinician hovers the proposal card.
     * BORROWS the handle — it must never be deleted here (commit adopts the
     * same handle). Absent in unit fakes (show/hide become no-ops).
     */
    readonly preview?: GeometryPreviewPort<C>;
}

/* ── Stage 1: resolve (target + units + invariants) ── */

/**
 * A resolve result, generic over the op's branded `validated` value. The
 * three failure arms are shared across every op; only the `validated` arm
 * stays per-op-branded, so a margin `Validated` cannot flow into `previewRing`.
 */
export type ValidateResult<V> =
    | { readonly kind: 'validated'; readonly validated: V }
    | {
          readonly kind: 'ambiguous';
          readonly reason: AmbiguityReason;
          readonly candidates: ReadonlyArray<ListedStructure>;
      }
    | {
          readonly kind: 'not-found';
          readonly suggestions: ReadonlyArray<ListedStructure>;
      }
    | { readonly kind: 'invalid'; readonly reason: string };

export function toListed(f: StructureFacts): ListedStructure {
    return {
        roiNumber: f.roiNumber,
        name: f.name,
        volumeCm3: f.volumeCm3,
        type: f.type,
    };
}

/**
 * Inclusive per-direction clinical bound (cm) shared by every margin-like op
 * (symmetric/asymmetric margin, ring). A larger value is a cm/mm mix-up or
 * clinically nonsensical; we REJECT and surface it rather than clamp
 * (CLAUDE.md: never silently clamp a clinical value).
 */
export const MAX_ABS_MARGIN_CM = 5;

/**
 * The portion of resolution that is identical for every structure-creating op
 * (single source for name-collision + the defence-in-depth
 * domain guards, so the rule cannot drift between ops). Returns a PLAIN
 * `StructureFacts` (never a brand) — each op still performs its own numeric
 * invariants and mints its own pipeline brand at its single cm→mm
 * choke-point, so per-op golden-test isolation and the structural
 * preview==apply guarantee are untouched. `opNoun`/`emptyHint` feed only the
 * model-facing guard messages.
 */
type SourceResolution =
    | { readonly kind: 'source'; readonly source: StructureFacts }
    | {
          readonly kind: 'ambiguous';
          readonly reason: AmbiguityReason;
          readonly candidates: ReadonlyArray<ListedStructure>;
      }
    | {
          readonly kind: 'not-found';
          readonly suggestions: ReadonlyArray<ListedStructure>;
      }
    | { readonly kind: 'invalid'; readonly reason: string };

/**
 * Resolve ONE structure reference and apply the defence-in-depth domain
 * guards (approved / point ROI / empty). The single source of the per-source
 * rule, shared by every structure-creating op AND by every boolean operand,
 * so the guard cannot drift. No new-structure-name concern (that is
 * {@link resolveSourceForNewStructure}'s, layered on top).
 */
export function resolveOneStructure(
    roi: string,
    structures: ReadonlyArray<StructureFacts>,
    listed: ReadonlyArray<ListedStructure>,
    opNoun: string,
    emptyHint: string
): SourceResolution {
    const resolution = resolveRoi(roi, listed);
    if (resolution.kind === 'ambiguous') {
        return {
            kind: 'ambiguous',
            reason: resolution.reason,
            candidates: resolution.candidates,
        };
    }
    if (resolution.kind === 'not-found') {
        return { kind: 'not-found', suggestions: resolution.suggestions };
    }

    const source = structures.find(
        (s) => s.roiNumber === resolution.structure.roiNumber
    );
    if (source === undefined) {
        return { kind: 'invalid', reason: 'resolved structure not found' };
    }
    // Defence-in-depth domain guards (CLAUDE.md: fail-fast on invalid state).
    // Approved structures are valid read-only SOURCES — every op creates a
    // NEW structure, so approval only ever gates a write target, never here.
    if (isPointRoiType(source.type)) {
        return {
            kind: 'invalid',
            reason: `'${source.name}' is a point ROI; a ${opNoun} is not defined for it`,
        };
    }
    if (source.isEmpty) {
        return {
            kind: 'invalid',
            reason: `'${source.name}' has no contours to ${emptyHint}`,
        };
    }
    return { kind: 'source', source };
}

export function resolveSourceForNewStructure(
    roi: string,
    newRoi: string,
    structures: ReadonlyArray<StructureFacts>,
    opNoun: string,
    emptyHint: string
): SourceResolution {
    // Project once, reuse for both name-collision and target resolution.
    const listed = structures.map(toListed);

    // New-structure name must be valid AND not collide with ANY existing
    // structure (case-insensitive) — operations create a NEW structure and
    // must never silently overwrite. Reuse the authoritative
    // validator so the rule cannot drift from the rest of the app.
    const [nameOk, nameError] = validateStructureName(
        newRoi,
        listed.map((l) => l.name)
    );
    if (!nameOk) {
        return {
            kind: 'invalid',
            reason: nameError ?? 'invalid new structure name',
        };
    }

    return resolveOneStructure(roi, structures, listed, opNoun, emptyHint);
}

/* ── Stage 2: preview (compute geometry ONCE) ── */

export function reasonOf(err: unknown): string {
    return err instanceof Error ? err.message : 'unknown error';
}

/**
 * The shared empty-result gate for every op's preview: an operation that
 * erodes the structure to nothing must be surfaced, never silently written.
 * Frees the computed texture (it will not be committed) and returns the
 * failure; returns undefined when the result is non-empty and may proceed.
 */
function rejectEmptyResult(
    computed: { readonly resultVolumeCm3: number; readonly handle: unknown },
    releaseComputed: ((handle: unknown) => void) | undefined,
    reason: string
): { readonly kind: 'failed'; readonly reason: string } | undefined {
    if (!(computed.resultVolumeCm3 > 0)) {
        releaseComputed?.(computed.handle);
        return { kind: 'failed', reason };
    }
    return undefined;
}

/**
 * The single-call preview body shared by every op that computes its geometry in
 * ONE engine call (margin / ring / asymmetric margin): compute once
 * (Result-not-throw), gate the empty result, and emit the UNBRANDED previewed
 * data. Each public `previewX` is a thin wrapper that supplies the op's engine
 * call + empty-reason and mints its OWN brand on the result — the brand cast
 * stays per-op so a margin preview cannot be passed to `commitRing`.
 */
export async function previewSingleCall<
    V extends { readonly sourceVolumeCm3: number; readonly newRoi: string },
>(
    validated: V,
    callEngine: (validated: V) => Promise<ComputedGeometry>,
    releaseComputed: ((handle: unknown) => void) | undefined,
    emptyReason: string
): Promise<
    | {
          readonly kind: 'previewed';
          readonly previewed: {
              readonly computed: ComputedGeometry;
              readonly newRoi: string;
              readonly volumeDeltaCm3: number;
          };
      }
    | { readonly kind: 'failed'; readonly reason: string }
> {
    let computed: ComputedGeometry;
    try {
        computed = await callEngine(validated);
    } catch (err) {
        // Result-not-throw across the boundary; the model is told.
        return { kind: 'failed', reason: reasonOf(err) };
    }
    const empty = rejectEmptyResult(computed, releaseComputed, emptyReason);
    if (empty) {
        return empty;
    }
    return {
        kind: 'previewed',
        previewed: {
            computed,
            newRoi: validated.newRoi,
            volumeDeltaCm3:
                computed.resultVolumeCm3 - validated.sourceVolumeCm3,
        },
    };
}

/* ── Stage 3: commit (write the ALREADY-computed geometry to a NEW ROI) ── */

export type CommitResult =
    | {
          readonly kind: 'applied';
          readonly newRoiNumber: number;
          readonly newRoi: string;
          readonly volumeCm3: number;
          readonly volumeDeltaCm3: number;
      }
    | { readonly kind: 'failed'; readonly reason: string };

/**
 * Adopt-or-release lifecycle for an eagerly-computed geometry handle, shared by
 * every op's proposal. apply() claim()s the handle before awaiting the commit;
 * reject() — and cancel, via reject — frees it after clearing the borrowing
 * overlay, at most once and never once claimed, so "release-at-most-once,
 * never-after-adopt" is one structural rule rather than four hand-maintained
 * copies of a use-after-free guard. claim() marks INTENT to adopt, not adoption
 * itself: a commit that fails before the destination ROI takes the texture
 * frees the unadopted handle in the commit seam (applyComputedGeometry), so the
 * guard must not.
 */
export function makeHandleGuard(
    computed: { readonly handle: unknown },
    deps: {
        readonly preview?: { hide(): void };
        readonly geometry: { releaseComputed?(handle: unknown): void };
    }
): { claim(): void; reject(): { status: 'rejected' } } {
    let claimed = false;
    return {
        claim() {
            claimed = true;
        },
        reject() {
            deps.preview?.hide();
            if (!claimed) {
                claimed = true;
                deps.geometry.releaseComputed?.(computed.handle);
            }
            return { status: 'rejected' };
        },
    };
}

/**
 * Write the ALREADY-computed geometry to a NEW structure. Private and generic
 * over the opaque geometry type so the choreography is single-source; the
 * PUBLIC per-op `commit*` functions keep their distinct branded
 * (`Previewed`/`RingPreviewed`) parameter, so the structural "commit consumes
 * only what preview produced" guarantee (no recompute path is expressible) is
 * unchanged.
 */
export async function commitComputed<C>(
    previewed: {
        readonly computed: C;
        readonly newRoi: string;
        readonly volumeDeltaCm3: number;
    },
    createNewStructure: (name: string, computed: C) => Promise<CreatedStructure>
): Promise<CommitResult> {
    let created: CreatedStructure;
    try {
        created = await createNewStructure(
            previewed.newRoi,
            previewed.computed
        );
    } catch (err) {
        return { kind: 'failed', reason: reasonOf(err) };
    }
    return {
        kind: 'applied',
        newRoiNumber: created.roiNumber,
        newRoi: previewed.newRoi,
        volumeCm3: created.volumeCm3,
        volumeDeltaCm3: previewed.volumeDeltaCm3,
    };
}
